import { useMemo, type ReactNode } from "react";

import { cn } from "../lib/cn";
import { buildQrPath } from "./qrMatrix";

const QR_PIXELS = { md: 192, lg: 240 } as const;

export type typeQrCodeProps = {
  /** What the code opens when scanned, usually a pool's invite link. */
  value: string;
  /** Read to a screen reader in place of the picture. */
  label: string;
  size?: keyof typeof QR_PIXELS | undefined;
  className?: string | undefined;
  "data-testid"?: string | undefined;
};

/**
 * A scannable QR code (ISSUE-1339).
 *
 * Always dark modules on a white tile, in light AND dark mode: plenty of
 * phone cameras will not read an inverted code, and a fan holding the screen
 * up to a friend has no way to know theirs is one of them. The white tile
 * carries the quiet zone, so the code scans on any page color.
 */
export const QrCode = ({
  value,
  label,
  size = "md",
  className,
  "data-testid": testId,
}: typeQrCodeProps): ReactNode => {
  const qr = useMemo(() => buildQrPath(value), [value]);
  const pixels = QR_PIXELS[size];
  return (
    <svg
      role="img"
      aria-label={label}
      width={pixels}
      height={pixels}
      viewBox={`0 0 ${qr.size} ${qr.size}`}
      shapeRendering="crispEdges"
      className={cn(
        "rounded-sm bg-neutral-0 text-neutral-1000 shadow-sm",
        className,
      )}
      data-testid={testId}
      data-qr-value={value}
    >
      <rect width={qr.size} height={qr.size} className="fill-neutral-0" />
      <path d={qr.path} fill="currentColor" />
    </svg>
  );
};
